import { useState } from 'react';
import { LogOut, Download, Trash2, CreditCard } from 'lucide-react';
import { supabase } from '../lib/supabase';
import useStore from '../store/useStore';
import { Card } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';

const EXPORT_KEYS = [
  'accounts', 'creditCards', 'investments', 'subscriptions',
  'cashFlowMonthly', 'netWorthHistory', 'budgetCategories', 'transactions',
  'habits', 'habitLog', 'dailyAssessments', 'todos', 'dailyPlanner',
  'bucketList', 'readingList', 'places', 'network',
  'bodyMetrics', 'weightLog', 'nutritionLog', 'workoutLog', 'liftTargets',
  'calorieTargets', 'paycheckSnapshots', 'userSettings',
  'balanceSheetMonthly', 'taxes',
  'sleepLog', 'dailySummaries', 'restingHeartRate', 'vo2Max',
  'activitySummaries', 'workoutSessions', 'exerciseHistory', 'bmiLog',
];

export default function Settings() {
  const session = useStore((s) => s.session);
  const userSettings = useStore((s) => s.userSettings);
  const updateUserSettings = useStore((s) => s.updateUserSettings);
  const [loggingOut, setLoggingOut] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [managingBilling, setManagingBilling] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    await supabase.auth.signOut();
  };

  const handleExport = () => {
    setExporting(true);
    try {
      const state = useStore.getState();
      const exportData = {};
      for (const key of EXPORT_KEYS) {
        if (state[key] != null) exportData[key] = state[key];
      }
      exportData.exportedAt = new Date().toISOString();
      exportData.email = session?.user?.email;

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'meridian-export.json';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const res = await fetch('/api/delete-account', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const { error } = await res.json();
        throw new Error(error || 'Failed to delete account');
      }
      await supabase.auth.signOut();
    } catch (err) {
      setDeleting(false);
      alert(err.message);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Settings" />

      <Card className="max-w-lg">
        <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-4">Account</h2>

        <div className="flex items-center justify-between py-2 border-b border-border/30 mb-4">
          <span className="text-sm text-text-secondary">Email</span>
          <span className="text-sm text-text-primary">{session?.user?.email}</span>
        </div>

        <button
          onClick={handleLogout}
          disabled={loggingOut}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-danger rounded-lg hover:bg-danger/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <LogOut className="w-4 h-4" />
          {loggingOut ? 'Logging out...' : 'Log out'}
        </button>
      </Card>

      <Card className="max-w-lg">
        <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-4">Subscription</h2>

        <div className="flex items-center justify-between py-2 border-b border-border/30 mb-4">
          <span className="text-sm text-text-secondary">Plan</span>
          <span className="text-sm text-text-primary font-medium">
            {userSettings.subscriptionActive ? 'Premium' : 'Free'}
          </span>
        </div>

        {userSettings.subscriptionActive && (
          <button
            onClick={async () => {
              setManagingBilling(true);
              try {
                const { data: { session: s } } = await supabase.auth.getSession();
                const res = await fetch('/api/create-portal', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${s.access_token}`,
                  },
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error);
                window.location.href = data.url;
              } catch (err) {
                alert(err.message);
                setManagingBilling(false);
              }
            }}
            disabled={managingBilling}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-text-primary bg-bg-tertiary border border-border rounded-lg hover:bg-bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CreditCard className="w-4 h-4" />
            {managingBilling ? 'Redirecting...' : 'Manage billing'}
          </button>
        )}
      </Card>

      <Card className="max-w-lg">
        <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-4">Automation</h2>

        <div className="space-y-3">
          <label className="flex items-center justify-between py-2 cursor-pointer">
            <div>
              <span className="text-sm text-text-primary">Auto-Snapshot</span>
              <p className="text-xs text-text-muted">Automatically save a balance sheet snapshot on app load</p>
            </div>
            <input
              type="checkbox"
              checked={userSettings.autoSnapshotEnabled ?? true}
              onChange={(e) => updateUserSettings({ autoSnapshotEnabled: e.target.checked })}
              className="w-4 h-4 rounded border-border text-accent focus:ring-accent"
            />
          </label>

          <label className="flex items-center justify-between py-2 cursor-pointer">
            <div>
              <span className="text-sm text-text-primary">Auto-Postings</span>
              <p className="text-xs text-text-muted">Auto-post paycheck and recurring transactions on app load</p>
            </div>
            <input
              type="checkbox"
              checked={userSettings.autoPostingsEnabled ?? true}
              onChange={(e) => updateUserSettings({ autoPostingsEnabled: e.target.checked })}
              className="w-4 h-4 rounded border-border text-accent focus:ring-accent"
            />
          </label>
        </div>
      </Card>

      <Card className="max-w-lg">
        <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-4">Data</h2>

        <p className="text-xs text-text-secondary mb-3">
          Download a copy of all your data as a JSON file.
        </p>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-text-primary bg-bg-tertiary border border-border rounded-lg hover:bg-bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exporting...' : 'Export my data'}
        </button>
      </Card>

      <Card className="max-w-lg border-danger/30">
        <h2 className="text-xs font-bold text-danger uppercase tracking-wider mb-4">Danger Zone</h2>

        {!showDeleteConfirm ? (
          <>
            <p className="text-xs text-text-secondary mb-3">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-danger border border-danger/50 rounded-lg hover:bg-danger/10 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete my account
            </button>
          </>
        ) : (
          <>
            <p className="text-xs text-text-secondary mb-3">
              This will permanently delete your account and all data across every category (finance, health, productivity, and more). This cannot be reversed.
            </p>
            <p className="text-xs text-text-secondary mb-2">
              Type <span className="text-text-primary font-mono font-medium">DELETE</span> to confirm:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="w-full bg-bg-tertiary border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-danger transition-colors mb-3"
            />
            <div className="flex gap-2">
              <button
                onClick={handleDeleteAccount}
                disabled={confirmText !== 'DELETE' || deleting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-danger rounded-lg hover:bg-danger/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Deleting...' : 'Permanently delete'}
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setConfirmText(''); }}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-text-secondary bg-bg-tertiary border border-border rounded-lg hover:bg-bg-secondary transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
